import { Container, Graphics, Text, TextStyleFontWeight } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { COLORS, INTRO_FONT, METRICS, drawPanel } from "./intro_theme"
import { LoginButton } from "./login_button"
import { LoginField } from "./login_field"

export type LoginMode = "guest" | "account"

interface LoginCardProps {
    /** A name typed with no password means "play as a guest called this". */
    onPlay: (playerName: string) => void
    onLogin: (username: string, password: string) => void
    /** A fresh "Guest 1234" for when nothing was typed. */
    guestName: string
    /**
     * The card keeps its middle on this line and grows about it, so switching to the sign-in fields
     * or adding an error row cannot push the bottom of the card off the screen.
     */
    centerY: number
    width?: number
}

/**
 * The login card: one panel that swaps between picking a guest name and signing in, rather than two
 * separate screens. Nothing outside the card moves when the mode changes, so the wordmark and the
 * background hold still.
 *
 * Layout runs top-down and the panel is sized to whatever the content ended up being, so adding a
 * row (the error line) grows the card instead of overflowing it.
 */
export class LoginCard extends Container {
    private panel = new Graphics()
    private content = new Container()
    private props: LoginCardProps

    private mode: LoginMode = "guest"
    private submitted = false
    /** Whether the card is on screen -- the fields' DOM nodes have to be told (see setDomVisible). */
    private domVisible = false

    private nameField?: LoginField
    private passwordField?: LoginField
    private errorText?: Text

    constructor(props: LoginCardProps) {
        super()
        this.props = props

        this.addChild(this.panel)
        this.addChild(this.content)

        this.layout()
    }

    get currentMode(): LoginMode {
        return this.mode
    }

    focus = () => {
        this.nameField?.focus()
    }

    /**
     * Follow the card on and off screen.
     *
     * Hiding the card by flipping `visible` on it or any ancestor is not enough on its own: the
     * fields are real DOM `<input>`s and the library only moves them from inside a render pass, so
     * the one it never gets to run leaves them on the page -- the "Pick a name, or play as a guest"
     * placeholder sitting over the next screen until the card is finally disposed. Every show and
     * hide of the card has to come through here.
     */
    setDomVisible = (visible: boolean) => {
        this.domVisible = visible
        this.nameField?.setDomVisible(visible)
        this.passwordField?.setDomVisible(visible)
    }

    setMode = (mode: LoginMode) => {
        if (this.mode === mode) {
            return
        }
        this.mode = mode
        this.layout()
        if (mode === "account" && this.nameField?.value) {
            this.passwordField?.focus()
        } else {
            this.focus()
        }
    }

    /**
     * Show a failure against the credentials that are still in the fields. The username is kept --
     * losing it on every wrong password is most of what made the old failure path miserable.
     */
    showError = (message: string) => {
        this.submitted = false
        this.mode = "account"
        this.layout({ error: message })
        this.passwordField?.setTone("error")
        this.passwordField?.focus()
    }

    dispose = () => {
        this.nameField?.dispose()
        this.passwordField?.dispose()
        this.nameField = undefined
        this.passwordField = undefined
        this.content.removeChildren()
        this.removeChildren()
        this.parent?.removeChild(this)
    }

    setWidth = (width: number) => {
        if ((this.props.width ?? METRICS.cardWidth) === width) return
        const focused = document.activeElement
        const passwordFocused = focused === this.passwordField?.input._dom_input
        const nameFocused = focused === this.nameField?.input._dom_input
        this.props.width = width
        this.layout({ error: this.errorText?.text })
        if (passwordFocused) this.passwordField?.focus()
        else if (nameFocused) this.nameField?.focus()
    }

    private layout = (opts: { error?: string } = {}) => {
        const keepName = this.nameField?.value
        const keepPassword = this.passwordField?.value

        this.nameField?.dispose()
        this.passwordField?.dispose()
        this.nameField = undefined
        this.passwordField = undefined
        this.content.removeChildren()
        this.errorText = undefined

        const cardWidth = this.props.width ?? METRICS.cardWidth
        const contentWidth = cardWidth - METRICS.cardPadding * 2
        const pad = METRICS.cardPadding
        let y = pad

        const place = (child: Container, top: number) => {
            child.x = pad
            child.y = top
            this.content.addChild(child)
        }

        if (this.mode === "account") {
            const heading = this.text("Welcome back", 18, COLORS.text, "500")
            place(heading, y)
            y += 34
        }

        this.nameField = new LoginField({
            width: contentWidth,
            label: "Player name",
            placeholder: this.mode === "guest" ? "Pick a name, or play as a guest" : undefined,
            autocomplete: "username",
            onEnter: () => (this.mode === "guest" ? this.play() : this.passwordField?.focus()),
            onEscape: () => this.mode === "account" && this.setMode("guest"),
        })
        if (keepName) {
            this.nameField.value = keepName
        }
        place(this.nameField, y)
        y += 24 + METRICS.fieldHeight

        if (this.mode === "account") {
            y += 16
            this.passwordField = new LoginField({
                width: contentWidth,
                label: "Password",
                autocomplete: "current-password",
                password: true,
                onInput: this.dimError,
                onEnter: this.submit,
                onEscape: () => this.setMode("guest"),
            })
            if (keepPassword) {
                this.passwordField.value = keepPassword
            }
            place(this.passwordField, y)
            y += 24 + METRICS.fieldHeight
        }

        if (opts.error) {
            y += 10
            this.errorText = this.text(opts.error, 14, COLORS.danger, "normal", contentWidth)
            place(this.errorText, y)
            y += this.errorText.height
        }

        y += METRICS.gutter

        if (this.mode === "guest") {
            const half = (contentWidth - METRICS.gutter) / 2

            const play = new LoginButton({
                label: "Play",
                width: half,
                variant: "primary",
                onClick: this.play,
            })
            place(play, y)

            const guest = new LoginButton({
                label: "Play as guest",
                width: half,
                variant: "ghost",
                onClick: () => this.props.onPlay(this.props.guestName),
            })
            guest.x = pad + half + METRICS.gutter
            guest.y = y
            this.content.addChild(guest)

            y += METRICS.buttonHeight + 24

            const rule = new Graphics()
            rule.beginFill(COLORS.panelRim, 0.28)
            rule.drawRect(0, 0, contentWidth, 1)
            rule.endFill()
            place(rule, y)
            y += 15

            const footer = new Container()
            const prompt = this.text("Already have an account?", 14, COLORS.muted)
            const link = this.text("Log in", 14, COLORS.accent, "500")
            link.x = prompt.width + 6
            link.interactive = true
            link.cursor = "pointer"
            const toAccount = () => this.setMode("account")
            link.on("click", toAccount)
            link.on("tap", toAccount)
            link.on("pointerover", () => (link.tint = COLORS.accentHi))
            link.on("pointerout", () => (link.tint = COLORS.accent))
            footer.addChild(prompt)
            footer.addChild(link)
            footer.x = pad + Math.round((contentWidth - (prompt.width + 6 + link.width)) / 2)
            footer.y = y
            this.content.addChild(footer)
            y += footer.height
        } else {
            const login = new LoginButton({
                label: "Log in",
                width: contentWidth - 86,
                variant: "primary",
                onClick: this.submit,
            })
            place(login, y)

            const back = this.text("Back", 14, COLORS.muted)
            back.interactive = true
            back.cursor = "pointer"
            const toGuest = () => this.setMode("guest")
            back.on("click", toGuest)
            back.on("tap", toGuest)
            back.on("pointerover", () => (back.tint = COLORS.text))
            back.on("pointerout", () => (back.tint = COLORS.muted))
            back.x = pad + contentWidth - 86 + Math.round((86 - back.width) / 2)
            back.y = y + Math.round((METRICS.buttonHeight - back.height) / 2)
            this.content.addChild(back)

            y += METRICS.buttonHeight
        }

        // A re-layout builds brand new fields. Keeping them hidden is ours to do -- showing them
        // is not: an input the library has not placed yet still sits at the page's top-left, so
        // revealing it by hand flashes the field in the corner. A visible card gets its fields from
        // the library's next render, which places and shows them in the same pass.
        if (!this.domVisible) {
            this.setDomVisible(false)
        }

        const height = y + pad
        drawPanel(this.panel, cardWidth, height)
        this.y = Math.round(this.props.centerY - height / 2)
    }

    /**
     * Fade the message once the player starts fixing the input, without re-laying out the card --
     * dropping the row would resize the panel and steal focus mid-keystroke.
     */
    private dimError = () => {
        if (this.errorText) {
            this.errorText.visible = false
        }
    }

    private text = (value: string, size: number, color: number, weight: TextStyleFontWeight = "normal", wrap?: number): Text => {
        const builder = new TextRenderer().fontFamily(INTRO_FONT).fontSize(size).color(color)
        const text = (wrap ? builder.wordWrap(wrap) : builder).render(value) as Text
        text.style.fontWeight = weight
        return text
    }

    private play = () => {
        if (this.submitted) {
            return
        }
        const name = this.nameField?.value || this.props.guestName
        this.submitted = true
        this.props.onPlay(name)
    }

    private submit = () => {
        if (this.submitted) {
            return
        }
        const username = this.nameField?.value
        const password = this.passwordField?.value

        if (!username) {
            this.layout({ error: "Enter your player name." })
            this.nameField?.setTone("error")
            this.nameField?.focus()
            return
        }
        if (!password) {
            this.layout({ error: "Enter your password." })
            this.passwordField?.setTone("error")
            this.passwordField?.focus()
            return
        }

        this.submitted = true
        this.props.onLogin(username, password)
    }
}
